import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Eye, Calendar, User, Fuel, Download, X,
  ChevronLeft, ChevronRight, Loader2, FileText, Printer,
  CheckCircle, Clock, XCircle, AlertCircle
} from 'lucide-react';
import API_BASE_URL from "../config";
import Swal from 'sweetalert2';
import { jsPDF } from 'jspdf';
import megalogo from '../Image/megalogo.png';

export default function FuelRequisitionApproval() {
  const [fuelRequests, setFuelRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [websiteInfo, setWebsiteInfo] = useState(null);
  const [approving, setApproving] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const [canApprove, setCanApprove] = useState(false);
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);

  // ── Store the resolved role parts & dept for filtering ──
  // These are set ONCE after fetching UserLevels, so no stale state issues
  const [resolvedFilter, setResolvedFilter] = useState(null);
  // resolvedFilter = { isAdmin: bool, roleParts: string[], dept: string|null }

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const FUEL_KEYWORDS = ['fuel', 'diesel', 'gasoline', 'petrol', 'kerosene'];

  // ── SINGLE filter function — uses resolvedFilter from state ────────
  const applyFilter = useCallback((requests, filter, search) => {
    if (!filter) return;

    let filtered = [...requests];

    console.log("=== APPLY FILTER ===");
    console.log("filter.isAdmin:", filter.isAdmin);
    console.log("filter.dept:", filter.dept);
    console.log("filter.role:", filter.role);

    if (!filter.isAdmin) {
      const roleParts = (filter.role || "")
        .split(/[\/,\-]+/)
        .map(p => p.trim().toLowerCase())
        .filter(Boolean);

      const dept = filter.dept?.toLowerCase().trim();

      console.log("roleParts:", roleParts);
      console.log("dept:", dept);
      console.log("--- All request departments ---");
      requests.forEach(req => {
        console.log(`  ID: ${req.id} | dept: "${req.department}" | match dept: ${dept && req.department?.toLowerCase().trim() === dept} | match role: ${roleParts.some(p => req.department?.toLowerCase().trim() === p)}`);
      });

      filtered = filtered.filter(req => {
        const reqDept = req.department?.toLowerCase().trim();
        return (
          (dept && reqDept === dept) ||
          roleParts.some(part => reqDept === part)
        );
      });

      console.log("filtered count:", filtered.length);
    }
    if (search) {
      const s = search.toLowerCase();
      filtered = filtered.filter(req =>
        req.controlNo?.toLowerCase().includes(s) ||
        req.requestorName?.toLowerCase().includes(s) ||
        req.plateNumber?.toLowerCase().includes(s) ||
        req.department?.toLowerCase().includes(s)
      );
    }

    setFilteredRequests(filtered);
    setCurrentPage(1);
  }, []);
  // ── Re-run filter whenever data, search, or resolvedFilter changes ─
  useEffect(() => {
    applyFilter(fuelRequests, resolvedFilter, searchTerm);
  }, [fuelRequests, resolvedFilter, searchTerm, applyFilter]);

  // ── Init: load user + fetch UserLevels ONCE ────────────────────────
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) { setLoading(false); return; }

    const userData = JSON.parse(storedUser);
    const fullName = `${userData.firstName} ${userData.lastName}`.trim();
    const dept = userData.department || userData.departmentName || userData.dept || null;
    const { userLevelId } = userData;

    console.log("=== LOGGED IN USER ===");
    console.log("Full Data:", userData);
    console.log("Name:", fullName);
    console.log("userLevelId:", userLevelId);
    console.log("Department:", dept);

    setCurrentUser({
      name: fullName,
      firstName: userData.firstName,
      lastName: userData.lastName,
      department: dept,
      role: "",
      username: userData.username || fullName, // ✅ idagdag ito
      userId: userData.id || userData.userId,  // ✅ idagdag din
    });

    if (!userLevelId) {
      console.warn("⚠️ No userLevelId found!");
      fetchFuelRequests();
      fetchWebsiteInfo();
      return;
    }

    // ✅ Same approach as Sidebar — fetch by userLevelId directly
    fetch(`${API_BASE_URL}api/UserLevels/${userLevelId}`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      })
      .then(levelData => {
        console.log("=== USER LEVEL DATA ===");
        console.log(levelData);
        console.log("userLevelName:", levelData.userLevelName);
        console.log("fuelApproval:", levelData.fuelApproval);
        console.log("accessViewAllData:", levelData.accessViewAllData);
        const role = levelData.userLevelName || "";
        const ALLOWED_ROLES = ["SYSTEM ADMIN", "SPAREPARTS"];
        const normalizedRole = role.toUpperCase().trim();
        const isSuperAdmin = ALLOWED_ROLES.includes(normalizedRole); const isAdmin = isSuperAdmin; // ← SYSTEM ADMIN lang ang makakakita ng lahat
        const canApp = isSuperAdmin || levelData.fuelApproval === true;

        // ✅ SYSTEM ADMIN = superadmin, makikita lahat at pwedeng mag-approve
        console.log("isSuperAdmin:", isSuperAdmin);
        console.log("canApprove:", canApp);
        console.log("isAdmin:", isAdmin);

        setCurrentUser(prev => ({ ...prev, role }));
        setCanApprove(canApp);
        setIsSystemAdmin(isAdmin);
        const positionId = userData.positionId || role || "";
        setResolvedFilter({ isAdmin, dept, role: positionId });
      })
      .catch(err => {
        console.error("❌ Failed to fetch user level:", err);
        setCanApprove(false);
        setIsSystemAdmin(false);
        setResolvedFilter({ isAdmin: false, roleParts: [], dept });
      });

    fetchFuelRequests();
    fetchWebsiteInfo();
  }, []);

  const fetchWebsiteInfo = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Website_Details`);
      if (!res.ok) throw new Error('Failed to fetch website info');
      const data = await res.json();
      const activeWebsite = data.find(w => w.isActive) || data[0];
      if (activeWebsite) setWebsiteInfo(activeWebsite);
    } catch (error) {
      console.error('Error fetching website info:', error);
    }
  };

  const fetchFuelRequests = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}api/FuelRequisition`);
      if (!response.ok) { setFuelRequests([]); setFilteredRequests([]); return; }
      const data = await response.json();
      const requestArray = Array.isArray(data) ? data : (data ? [data] : []);
      setFuelRequests(requestArray);
      // filteredRequests will be set by the useEffect above
    } catch (error) {
      console.error('Error fetching fuel requests:', error);
      setFuelRequests([]); setFilteredRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setShowModal(true);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // APPROVE
  const postActivityFeed = async (fuelHeaderId, content) => {
    try {
      await fetch(`${API_BASE_URL}api/ActivityFeed`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fuelHeaderId,
          mrHeaderId: null,
          prHeaderId: null,
          poHeaderId: null,
          quotationId: null,
          content,
          feedType: "text",
          createdBy: currentUser?.userId || 1,
          createdByName: currentUser?.username || "System",
        }),
      });
    } catch (err) {
      console.warn("⚠️ Activity feed post failed (non-blocking):", err);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // APPROVE
  // ─────────────────────────────────────────────────────────────────────────────
  const handleApprove = async () => {
    if (!selectedRequest) return;
    if (!currentUser) {
      Swal.fire({ title: 'Error!', text: 'You must be logged in to approve requests.', icon: 'error', confirmButtonColor: '#ef4444' });
      return;
    }
    if (!canApprove) {
      Swal.fire({ title: 'No Permission!', text: 'Your account does not have fuel approval access.', icon: 'error', confirmButtonColor: '#ef4444' });
      return;
    }

    const litersToDeduct = parseFloat(selectedRequest.actualLiters) || 0;
    if (litersToDeduct <= 0) {
      Swal.fire({ title: 'Invalid Liters', text: 'Actual liters must be greater than 0 before approving.', icon: 'warning', confirmButtonColor: '#f97316' });
      return;
    }

    let totalAvailableFuel = 0;
    try {
      const invRes = await fetch(`${API_BASE_URL}api/InventoryData`);
      if (invRes.ok) {
        const invData = await invRes.json();
        const fuelItems = invData.filter(inv =>
          FUEL_KEYWORDS.some(kw =>
            inv.itemDescription?.toLowerCase().includes(kw) ||
            inv.itemCode?.toLowerCase().includes(kw)
          ) && (Number(inv.availableQty) || 0) > 0
        );
        totalAvailableFuel = fuelItems.reduce((sum, inv) => sum + (Number(inv.availableQty) || 0), 0);
      }
    } catch (_) { }

    if (totalAvailableFuel > 0 && totalAvailableFuel < litersToDeduct) {
      Swal.fire({
        title: '⛽ Insufficient Fuel Stock!',
        html: `
        <div style="text-align: left; font-size: 14px;">
          <p>Cannot approve — not enough fuel in inventory.</p><br/>
          <table style="width:100%; border-collapse: collapse;">
            <tr style="background:#fef3c7;"><td style="padding:6px 10px; font-weight:600;">Requested (Actual):</td><td style="padding:6px 10px; color:#d97706; font-weight:700;">${litersToDeduct} L</td></tr>
            <tr style="background:#fef2f2;"><td style="padding:6px 10px; font-weight:600;">Available in Stock:</td><td style="padding:6px 10px; color:#dc2626; font-weight:700;">${totalAvailableFuel} L</td></tr>
            <tr style="background:#fff1f2;"><td style="padding:6px 10px; font-weight:600;">Shortfall:</td><td style="padding:6px 10px; color:#be123c; font-weight:700;">${(litersToDeduct - totalAvailableFuel).toFixed(2)} L</td></tr>
          </table><br/>
          <p style="color:#6b7280; font-size:12px;">Please replenish fuel inventory before approving this request.</p>
        </div>`,
        icon: 'error', confirmButtonColor: '#ef4444', confirmButtonText: 'Understood'
      });
      return;
    }

    // ✅ I-check muna ang existing GI sa naka-load na fuelRequests state
    // ✅ I-check muna ang existing GI sa naka-load na fuelRequests state
    const requestDateStr = selectedRequest.requestDate
      ? new Date(selectedRequest.requestDate).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0];

    const sameDay = fuelRequests.find(r =>
      r.gI_Fuel_No &&
      r.gI_Fuel_No !== '' &&
      r.requestDate &&
      new Date(r.requestDate).toISOString().split('T')[0] === requestDateStr
    );
    const existingGiNumber = sameDay?.gI_Fuel_No || null;

    // ✅ Kung wala pang existing — i-fetch ang next GI number para maipakita
    let previewGiNumber = existingGiNumber;
    if (!previewGiNumber) {
      try {
        const giPreviewRes = await fetch(`${API_BASE_URL}api/FuelRequisition/existing-gi?date=${requestDateStr}`);
        const giPreviewData = giPreviewRes.ok ? await giPreviewRes.json() : { giNumber: null };
        previewGiNumber = giPreviewData.giNumber;

        // Kung wala talaga — compute from backend next number
        if (!previewGiNumber) {
          const allGIRes = await fetch(`${API_BASE_URL}api/FuelRequisition`);
          if (allGIRes.ok) {
            const allData = await allGIRes.json();
            const allGI = allData
              .filter(r => r.gI_Fuel_No && r.gI_Fuel_No !== '')
              .map(r => parseInt(r.gI_Fuel_No))
              .filter(n => !isNaN(n));
            const maxNum = allGI.length > 0 ? Math.max(...allGI) : 100000;
            previewGiNumber = `${maxNum + 1}`;
          }
        }
      } catch (_) { previewGiNumber = 'TBD'; }
    }

    // ✅ Show confirmation — nakikita na ang GI number
    const result = await Swal.fire({
      title: 'Approve Fuel Request?',
      html: `
      <div style="text-align: left; font-size: 14px;">
        <p><strong>Control No:</strong> ${selectedRequest.controlNo}</p>
        <p><strong>Requestor:</strong> ${selectedRequest.requestorName}</p>
        <p><strong>Plate #:</strong> ${selectedRequest.plateNumber}</p>
        <hr style="margin: 10px 0;">
        <p><strong>Qty Requested:</strong> ${selectedRequest.quantityLiters} L</p>
        <p><strong>Actual Liters:</strong> <span style="color:#16a34a; font-weight:700;">${litersToDeduct} L</span></p>
        <p><strong>Available Stock:</strong> <span style="color:#2563eb; font-weight:700;">${totalAvailableFuel} L</span></p>
        <hr style="margin: 10px 0;">
<p><strong>GI Number:</strong> <span style="color:#7c3aed; font-weight:700; font-size:16px;">${previewGiNumber}</span></p>        <p><strong>Approver:</strong> ${currentUser.name}</p>
        <p style="color:#f97316; font-size:12px; margin-top:8px;">⚠️ This will deduct <strong>${litersToDeduct}L</strong> from fuel inventory.</p>
      </div>`,
      icon: 'question', showCancelButton: true,
      confirmButtonColor: '#22c55e', cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Approve & Deduct', cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    setApproving(true);
    try {
      const frRes = await fetch(`${API_BASE_URL}api/FuelRequisition/next-fr-number`);
      if (!frRes.ok) throw new Error('Failed to generate FR number');
      const frData = await frRes.json();
      const generatedFRNumber = frData.frNumber;

      const deductRes = await fetch(`${API_BASE_URL}api/InventoryData/deduct-fuel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          litersToDeduct,
          frNumber: generatedFRNumber,
          controlNo: selectedRequest.controlNo,
          plateNumber: selectedRequest.plateNumber,
          deductedBy: currentUser.name,
          existingGiNumber,
        })
      });
      if (!deductRes.ok) {
        const errData = await deductRes.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to deduct fuel from inventory.');
      }
      const deductData = await deductRes.json();
      const deductionLog = deductData.breakdown || [];
      const giNumber = deductData.giNumber;

      const approveRes = await fetch(`${API_BASE_URL}api/FuelRequisition/${selectedRequest.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...selectedRequest,
          approvedBy: currentUser.name,
          approvedBySignature: null,
          fR_Number: generatedFRNumber,
          gI_Fuel_No: giNumber,
          approvedByVisor: true,
          status: 'Approved'
        })
      });
      if (!approveRes.ok) throw new Error('Inventory deducted but failed to update requisition status. Contact admin.');

      const user = JSON.parse(localStorage.getItem("user") || "{}");
      await fetch(`${API_BASE_URL}api/Logs`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "APPROVE", module: "Fuel Requisition",
          description: `Approved FR #${selectedRequest.controlNo} — FR#: ${generatedFRNumber}, GI#: ${giNumber}, Requestor: ${selectedRequest.requestorName}, Plate: ${selectedRequest.plateNumber}, Deducted: ${litersToDeduct}L.`,
          userId: user?.id || user?.userId, username: user?.username, fullName: currentUser.name || user?.username,
        }),
      }).catch(() => { });

      await postActivityFeed(
        selectedRequest.id,
        `✅ Fuel Requisition Approved by ${currentUser?.username}. FR Number: ${generatedFRNumber}. GI#: ${giNumber}. Deducted: ${litersToDeduct}L from inventory.`
      );

      await Swal.fire({
        title: '✅ Approved!',
        html: `<div style="font-size:14px;">
          <p>Fuel requisition approved successfully.</p><br/>
          <table style="width:100%; border-collapse:collapse; font-size:13px;">
            <tr style="background:#f0fdf4;">
              <td style="padding:6px 10px; font-weight:600; color:#374151;">GI Number:</td>
              <td style="padding:6px 10px; color:#1d4ed8; font-weight:700; font-size:15px;">${giNumber}</td>
            </tr>
            <tr style="background:#eff6ff;">
              <td style="padding:6px 10px; font-weight:600; color:#374151;">FR Number:</td>
              <td style="padding:6px 10px; color:#1d4ed8; font-weight:700;">${generatedFRNumber}</td>
            </tr>
            <tr style="background:#f0fdf4;">
              <td style="padding:6px 10px; font-weight:600; color:#374151;">Deducted:</td>
              <td style="padding:6px 10px; color:#16a34a; font-weight:700;">${litersToDeduct}L</td>
            </tr>
          </table>
          <p style="color:#6b7280; font-size:11px; margin-top:8px;">${deductionLog.join('<br/>')}</p>
        </div>`,
        icon: 'success', confirmButtonColor: '#3b82f6'
      });

      await fetchFuelRequests();
      setShowModal(false);
    } catch (error) {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      await fetch(`${API_BASE_URL}api/Logs`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "APPROVE-FAILED", module: "Fuel Requisition",
          description: `Failed to approve FR #${selectedRequest.controlNo}. Error: ${error.message}`,
          userId: user?.id || user?.userId, username: user?.username, fullName: currentUser.name || user?.username,
        }),
      }).catch(() => { });
      Swal.fire({ title: 'Approval Failed!', text: error.message || 'Something went wrong.', icon: 'error', confirmButtonColor: '#ef4444' });
    } finally {
      setApproving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // REJECT
  // ─────────────────────────────────────────────────────────────────────────────
  const handleReject = async () => {
    if (!selectedRequest) return;
    if (!currentUser) {
      Swal.fire({ title: 'Error!', text: 'You must be logged in to reject requests.', icon: 'error', confirmButtonColor: '#ef4444' });
      return;
    }
    if (!canApprove) {
      Swal.fire({ title: 'No Permission!', text: 'Your account does not have fuel approval access.', icon: 'error', confirmButtonColor: '#ef4444' });
      return;
    }

    const result = await Swal.fire({
      title: 'Reject Request?',
      html: `
      <div style="text-align: left;">
        <p><strong>Control No:</strong> ${selectedRequest.controlNo}</p>
        <p><strong>Requestor:</strong> ${selectedRequest.requestorName}</p>
        <hr style="margin: 10px 0;">
        <p><strong>Rejected By:</strong> ${currentUser.name}</p>
      </div>`,
      input: 'textarea', inputLabel: 'Rejection Reason', inputPlaceholder: 'Enter reason for rejection...',
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#ef4444', cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Reject', cancelButtonText: 'Cancel',
      inputValidator: (value) => { if (!value) return 'Please provide a reason for rejection'; }
    });

    if (!result.isConfirmed) return;

    setApproving(true);
    try {
      const response = await fetch(`${API_BASE_URL}api/FuelRequisition/${selectedRequest.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...selectedRequest,
          rejectedBy: currentUser.name,
          rejectionDate: new Date().toISOString(),
          rejectionReason: result.value,
          status: 'Rejected'
        })
      });
      if (!response.ok) throw new Error('Failed to reject');

      // 🔥 Log
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      await fetch(`${API_BASE_URL}api/Logs`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "REJECT", module: "Fuel Requisition",
          description: `Rejected Fuel Requisition #${selectedRequest.controlNo} (ID: ${selectedRequest.id}) requested by ${selectedRequest.requestorName}. Reason: ${result.value}`,
          userId: user?.id || user?.userId, username: user?.username, fullName: currentUser.name || user?.username,
        }),
      }).catch(err => console.error("Log failed:", err));

      // 🔥 Activity Feed
      await postActivityFeed(
        selectedRequest.id,
        `❌ Fuel Requisition Rejected by ${currentUser?.username}. Reason: ${result.value}`
      );

      await Swal.fire({ title: 'Rejected!', text: 'Fuel requisition has been rejected.', icon: 'success', confirmButtonColor: '#3b82f6' });
      await fetchFuelRequests();
      setShowModal(false);
    } catch (error) {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      await fetch(`${API_BASE_URL}api/Logs`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "REJECT-FAILED", module: "Fuel Requisition",
          description: `Failed to reject Fuel Requisition #${selectedRequest.controlNo} (ID: ${selectedRequest.id}). Error: ${error.message}`,
          userId: user?.id || user?.userId, username: user?.username, fullName: currentUser.name || user?.username,
        }),
      }).catch(err => console.error("Log failed:", err));

      Swal.fire({ title: 'Error!', text: 'Failed to reject the request.', icon: 'error', confirmButtonColor: '#ef4444' });
    } finally {
      setApproving(false);
    }
  };

  const isApproved = (request) =>
    request.approvedByVisor === true || request.approvedByVisor === 1;

  const getStatusBadge = (request) => {
    if (isApproved(request)) {
      return <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-800 px-3 py-1.5 rounded-lg text-xs font-bold border border-green-200"><CheckCircle className="w-3.5 h-3.5" /> Approved</span>;
    } else if (request.status === 'Rejected') {
      return <span className="inline-flex items-center gap-1.5 bg-red-100 text-red-800 px-3 py-1.5 rounded-lg text-xs font-bold border border-red-200"><XCircle className="w-3.5 h-3.5" /> Rejected</span>;
    } else {
      return <span className="inline-flex items-center gap-1.5 bg-yellow-100 text-yellow-800 px-3 py-1.5 rounded-lg text-xs font-bold border border-yellow-200"><Clock className="w-3.5 h-3.5" /> Pending</span>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return timeString;
  };

  const exportToPDF = (request) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 14;
    let y = 14;

    try { doc.addImage(megalogo, 'PNG', margin, y, 25, 13); } catch (err) { }
    doc.setFontSize(11); doc.setFont(undefined, 'bold'); doc.setTextColor(20, 20, 20);
    doc.text(websiteInfo?.websiteName || 'MEGASOFT HYGIENIC PRODUCTS, INC.', pageWidth / 2, y + 5, { align: 'center' });
    doc.setFontSize(7); doc.setFont(undefined, 'italic'); doc.setTextColor(100, 100, 100);
    doc.text(websiteInfo?.websiteSlogan || 'A MEGASOFT IN EVERY HOME', pageWidth / 2, y + 10, { align: 'center' });
    if (websiteInfo?.websiteContact) doc.text(websiteInfo.websiteContact, pageWidth / 2, y + 14, { align: 'center' });
    doc.setDrawColor(180, 180, 180); doc.setLineWidth(0.3);
    doc.rect(pageWidth - margin - 42, y, 42, 16);
    doc.setFontSize(6); doc.setFont(undefined, 'normal'); doc.setTextColor(60, 60, 60);
    doc.text('DOCUMENT CODE: SPR-F-009', pageWidth - margin - 40, y + 4.5);
    doc.text('ISSUE NO: 1  |  REVISION NO: 0', pageWidth - margin - 40, y + 8.5);
    doc.text(`DATE: ${new Date().toLocaleDateString()}`, pageWidth - margin - 40, y + 12.5);
    y += 20;
    doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.4);
    doc.line(margin, y, pageWidth - margin, y);
    y += 7;
    doc.setFontSize(12); doc.setFont(undefined, 'bold'); doc.setTextColor(30, 30, 30);
    doc.text('FUEL REQUISITION FORM', pageWidth / 2, y, { align: 'center' });
    doc.setDrawColor(30, 64, 175); doc.setLineWidth(0.8);
    doc.line(65, y + 2, 145, y + 2);
    const statusText = isApproved(request) ? 'APPROVED' : request.status === 'Rejected' ? 'REJECTED' : 'PENDING';
    const statusColor = isApproved(request) ? [34, 120, 60] : request.status === 'Rejected' ? [180, 40, 40] : [140, 100, 0];
    doc.setFillColor(...statusColor);
    doc.roundedRect(pageWidth - margin - 28, y - 5, 28, 7, 1, 1, 'F');
    doc.setFontSize(6.5); doc.setTextColor(255, 255, 255); doc.setFont(undefined, 'bold');
    doc.text(statusText, pageWidth - margin - 14, y - 0.8, { align: 'center' });
    y += 9;
    doc.setDrawColor(180, 180, 180); doc.setFillColor(247, 247, 247); doc.setLineWidth(0.3);
    doc.rect(margin, y, pageWidth - 2 * margin, 9, 'FD');
    doc.setFontSize(8); doc.setFont(undefined, 'bold'); doc.setTextColor(60, 60, 60);
    doc.text('CONTROL #:', margin + 3, y + 6);
    doc.setFontSize(9); doc.setTextColor(30, 64, 175);
    doc.text(request.controlNo || '—', margin + 27, y + 6);
    y += 13;
    doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.setTextColor(80, 80, 80);
    doc.text('REQUESTOR INFORMATION', margin, y);
    doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.3);
    doc.line(margin, y + 1.5, pageWidth - margin, y + 1.5);
    y += 5;
    const lCol = margin + 2; const rCol = pageWidth / 2 + 5;
    const drawField = (label, value, x, fy) => {
      doc.setFontSize(6.5); doc.setFont(undefined, 'normal'); doc.setTextColor(130, 130, 130);
      doc.text(label, x, fy);
      doc.setFontSize(8.5); doc.setFont(undefined, 'bold'); doc.setTextColor(20, 20, 20);
      doc.text(value || '—', x, fy + 5);
    };
    drawField('Requestor Name', request.requestorName, lCol, y);
    drawField('Department', request.department || 'N/A', rCol, y);
    y += 11;
    drawField('Plate Number', request.plateNumber, lCol, y);
    drawField('Date & Time', `${formatDate(request.requestDate)}  |  ${formatTime(request.requestTime)}`, rCol, y);
    y += 14;
    doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.setTextColor(80, 80, 80);
    doc.text('FUEL DETAILS', margin, y);
    doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.3);
    doc.line(margin, y + 1.5, pageWidth - margin, y + 1.5);
    y += 5;
    const colW = (pageWidth - 2 * margin) / 4;
    doc.setFillColor(240, 240, 240); doc.setDrawColor(210, 210, 210); doc.setLineWidth(0.2);
    doc.rect(margin, y, pageWidth - 2 * margin, 7, 'FD');
    doc.setFontSize(6.5); doc.setFont(undefined, 'bold'); doc.setTextColor(70, 70, 70);
    doc.text('DESCRIPTION', margin + 3, y + 5);
    doc.text('LEVEL', margin + colW + 3, y + 5);
    doc.text('ACTUAL LITERS', margin + colW * 2 + 3, y + 5);
    doc.text('TRUCK ODOMETER', margin + colW * 3 + 3, y + 5);
    y += 7;
    doc.setFillColor(252, 252, 252); doc.setDrawColor(210, 210, 210);
    doc.rect(margin, y, pageWidth - 2 * margin, 8, 'FD');
    doc.setFontSize(8); doc.setFont(undefined, 'normal'); doc.setTextColor(20, 20, 20);
    doc.text(String(request.description || '—'), margin + 3, y + 5.5);
    doc.text(String(request.level || '—'), margin + colW + 3, y + 5.5);
    doc.text(String(request.actualLiters ?? '—'), margin + colW * 2 + 3, y + 5.5);
    doc.text(String(request.truckOdometer ?? '—'), margin + colW * 3 + 3, y + 5.5);
    y += 10;
    doc.setFillColor(247, 247, 247); doc.setDrawColor(210, 210, 210);
    doc.rect(margin, y, pageWidth - 2 * margin, 14, 'FD');
    doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.setTextColor(80, 80, 80);
    doc.text('Quantity of Liters Requested:', margin + 3, y + 5.5);
    doc.text('Flow Meter Beg:', margin + 3, y + 11);
    doc.text('Flow Meter End:', pageWidth / 2 + 3, y + 11);
    doc.setFont(undefined, 'normal'); doc.setTextColor(20, 20, 20); doc.setFontSize(8.5);
    doc.text(String(request.quantityLiters ?? '—'), margin + 57, y + 5.5);
    doc.text(String(request.flowMeterBeg ?? '—'), margin + 35, y + 11);
    doc.text(String(request.flowMeterEnd ?? '—'), pageWidth / 2 + 32, y + 11);
    y += 18;
    if (request.purpose) {
      const purposeLines = doc.splitTextToSize(request.purpose, pageWidth - 2 * margin - 20);
      const purposeH = Math.max(12, purposeLines.length * 5 + 8);
      doc.setFillColor(247, 247, 247); doc.setDrawColor(210, 210, 210); doc.setLineWidth(0.2);
      doc.rect(margin, y, pageWidth - 2 * margin, purposeH, 'FD');
      doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.setTextColor(80, 80, 80);
      doc.text('PURPOSE:', margin + 3, y + 5.5);
      doc.setFont(undefined, 'normal'); doc.setTextColor(20, 20, 20); doc.setFontSize(7.5);
      doc.text(purposeLines, margin + 22, y + 5.5);
      y += purposeH + 6;
    }
    doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.setTextColor(80, 80, 80);
    doc.text('APPROVALS & SIGNATURES', margin, y);
    doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.3);
    doc.line(margin, y + 1.5, pageWidth - margin, y + 1.5);
    y += 6;
    const sigW = (pageWidth - 2 * margin - 5) / 2; const sigH = 22;
    const drawSigBox = (x, boxY, label, name) => {
      doc.setFillColor(255, 255, 255); doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.25);
      doc.rect(x, boxY, sigW, sigH, 'FD');
      doc.setFontSize(6.5); doc.setFont(undefined, 'bold'); doc.setTextColor(80, 80, 80);
      doc.text(label, x + 3, boxY + 5);
      doc.setDrawColor(100, 100, 100); doc.setLineWidth(0.35);
      doc.line(x + 3, boxY + 16, x + sigW - 3, boxY + 16);
      if (name) { doc.setFontSize(8); doc.setFont(undefined, 'bold'); doc.setTextColor(20, 20, 20); doc.text(name, x + 3, boxY + 14); }
      doc.setFontSize(5.5); doc.setFont(undefined, 'italic'); doc.setTextColor(140, 140, 140);
      doc.text('Signature & Date', x + 3, boxY + 20);
    };
    drawSigBox(margin, y, 'REQUESTED BY', request.requestedBy || request.requestorName);
    drawSigBox(margin + sigW + 5, y, 'NOTED BY', request.notedBy);
    y += sigH + 4;
    drawSigBox(margin, y, 'APPROVED BY', request.approvedBy);
    drawSigBox(margin + sigW + 5, y, 'GAS MAN / ASSIST BY', request.gasManAssistBy);
    y += sigH + 6;
    doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.3);
    doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
    doc.setFontSize(6); doc.setFont(undefined, 'italic'); doc.setTextColor(150, 150, 150);
    doc.text(`Generated on ${new Date().toLocaleString()}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
    doc.text(`Control No: ${request.controlNo}  |  SPR-F-009`, pageWidth / 2, pageHeight - 4, { align: 'center' });
    doc.save(`Fuel_Requisition_${request.controlNo}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRequests = filteredRequests.slice(startIndex, endIndex);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 p-6">
      <div className="max-w-8xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-xl shadow-lg shadow-blue-200">
              <Fuel className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Fuel Requisition Approval</h1>
              <p className="text-gray-500 mt-1">Review and approve fuel requests</p>
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by Control No, Requestor, Plate #, Department..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-all"
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600 font-medium">Loading fuel requests...</p>
              </div>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-20">
              <Fuel className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg font-medium">No fuel requests found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Control No.</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Date</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Requestor</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Plate #</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Department</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">Qty (L)</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">ODO</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {currentRequests.map((request) => (
                      <tr key={request.id} className="hover:bg-blue-50 transition-colors">
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-800 px-3 py-1.5 rounded-lg text-xs font-bold border border-blue-200">
                            <FileText className="w-3.5 h-3.5" />{request.controlNo}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium text-gray-900">{formatDate(request.requestDate)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium text-gray-900">{request.requestorName}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4"><span className="text-sm font-mono font-bold text-gray-900">{request.plateNumber}</span></td>
                        <td className="px-6 py-4"><span className="text-sm font-medium text-gray-700">{request.department || 'N/A'}</span></td>
                        <td className="px-6 py-4 text-center">
                          <span className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-blue-200">
                            <Fuel className="w-3.5 h-3.5" />{request.quantityLiters || 0} L
                          </span>
                        </td>

                        {/* ODO Defective badge */}
                        <td className="px-6 py-4 text-center">
                          {request.odo_Defective ? (
                            <div className="flex flex-col items-center gap-1">
                              <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-2.5 py-1 rounded-lg text-xs font-bold border border-red-200">
                                ⚠️ Defective
                              </span>
                              {request.odo_DefectiveNo && (
                                <span className="text-xs text-gray-400 font-mono">{request.odo_DefectiveNo}</span>
                              )}
                            </div>
                          ) : (
                            <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-500 px-2.5 py-1 rounded-lg text-xs font-medium border border-gray-200">
                              OK
                            </span>
                          )}
                        </td>

                        <td className="px-6 py-4 text-center">{getStatusBadge(request)}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center">
                            <button onClick={() => handleViewRequest(request)}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all shadow-md text-sm font-semibold">
                              <Eye className="w-4 h-4" /> View
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Showing <span className="font-bold text-gray-900">{startIndex + 1}</span> to{' '}
                    <span className="font-bold text-gray-900">{Math.min(endIndex, filteredRequests.length)}</span> of{' '}
                    <span className="font-bold text-gray-900">{filteredRequests.length}</span> requests
                  </p>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1}
                      className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-all flex items-center gap-1">
                      <ChevronLeft className="w-4 h-4" /> Previous
                    </button>
                    <span className="text-sm font-medium px-4">{currentPage} / {totalPages}</span>
                    <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages}
                      className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-all flex items-center gap-1">
                      Next <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-blue-700 to-blue-800 text-white px-8 py-6 flex-shrink-0">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-white/20 p-2 rounded-lg"><FileText className="w-6 h-6" /></div>
                    <h2 className="text-2xl font-bold">Fuel Requisition Approval</h2>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="bg-white/10 inline-block px-4 py-1.5 rounded-lg border border-white/20">
                      <p className="text-sm">Control No: <span className="font-bold text-base ml-2">{selectedRequest.controlNo}</span></p>
                    </div>
                    {getStatusBadge(selectedRequest)}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => exportToPDF(selectedRequest)}
                    className="p-2.5 hover:bg-white/20 rounded-lg transition-all border border-white/20" title="Download PDF">
                    <Download className="h-5 w-5" />
                  </button>
                  <button onClick={() => setShowModal(false)}
                    className="p-2.5 hover:bg-white/20 rounded-lg transition-all border border-white/20">
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-8 bg-gray-50 flex-1 overflow-y-auto">
              <div className="bg-white border-2 border-blue-100 rounded-xl p-6 mb-5 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <User className="w-5 h-5 text-blue-600" />
                  <h3 className="text-base font-bold text-gray-900">Requestor Information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div><p className="text-xs font-semibold text-gray-500 uppercase mb-1">Requestor Name</p><p className="text-base font-bold text-gray-900">{selectedRequest.requestorName}</p></div>
                  <div><p className="text-xs font-semibold text-gray-500 uppercase mb-1">Plate Number</p><p className="text-base font-bold text-gray-900 font-mono">{selectedRequest.plateNumber}</p></div>
                  <div><p className="text-xs font-semibold text-gray-500 uppercase mb-1">Department</p><p className="text-base font-bold text-gray-900">{selectedRequest.department || 'N/A'}</p></div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Date & Time</p>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-blue-600" />
                      <p className="text-base font-bold text-gray-900">{formatDate(selectedRequest.requestDate)} <span className="text-blue-500">•</span> {formatTime(selectedRequest.requestTime)}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-5 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <Fuel className="w-5 h-5 text-blue-600" />
                  <h3 className="text-base font-bold text-gray-900">Fuel Details</h3>
                </div>
                <div className="bg-blue-700 text-white rounded-xl p-4 mb-4 shadow-md">
                  <p className="text-sm font-semibold opacity-80">Quantity Requested</p>
                  <p className="text-3xl font-bold">{selectedRequest.quantityLiters || 0} <span className="text-xl font-normal">LITERS</span></p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { label: 'Description', value: selectedRequest.description || 'N/A' },
                    { label: 'Level', value: selectedRequest.level || 'N/A' },
                    { label: 'Actual Liters', value: `${selectedRequest.actualLiters || 0} L` },
                  ].map((item, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-1">{item.label}</p>
                      <p className="text-sm font-bold text-gray-900">{item.value}</p>
                    </div>
                  ))}

                  {/* Truck Odometer — with defective support */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Truck Odometer</p>
                    {selectedRequest.odo_Defective ? (
                      <div className="flex flex-col gap-1">
                        <span className="inline-flex items-center gap-1.5 bg-red-100 text-red-700 px-2.5 py-1 rounded-lg text-xs font-bold border border-red-200 w-fit">
                          ⚠️ ODO Defective
                        </span>
                        {selectedRequest.odo_DefectiveNo && (
                          <span className="text-xs text-gray-500 font-mono">Ref: {selectedRequest.odo_DefectiveNo}</span>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm font-bold text-gray-900">{selectedRequest.truckOdometer || 0}</p>
                    )}
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 col-span-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Flow Meter</p>
                    <div className="flex items-center gap-4">
                      <div><span className="text-xs text-gray-500">Beg:</span> <span className="text-sm font-bold ml-1">{selectedRequest.flowMeterBeg || 0}</span></div>
                      <div className="h-5 w-px bg-gray-300"></div>
                      <div><span className="text-xs text-gray-500">End:</span> <span className="text-sm font-bold ml-1">{selectedRequest.flowMeterEnd || 0}</span></div>
                    </div>
                  </div>
                </div>
              </div>

              {selectedRequest.purpose && (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-5 mb-5 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-blue-700" />
                    <h3 className="text-sm font-bold text-blue-900 uppercase">Purpose</h3>
                  </div>
                  <p className="text-sm text-gray-800 leading-relaxed">{selectedRequest.purpose}</p>
                </div>
              )}

              <div className={`border-2 rounded-xl p-6 shadow-sm ${isApproved(selectedRequest) ? 'bg-green-50 border-green-200' : selectedRequest.status === 'Rejected' ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-4">
                  {isApproved(selectedRequest) ? <CheckCircle className="w-6 h-6 text-green-600" /> : selectedRequest.status === 'Rejected' ? <XCircle className="w-6 h-6 text-red-600" /> : <AlertCircle className="w-6 h-6 text-yellow-600" />}
                  <h3 className="text-base font-bold text-gray-900">Approval Status</h3>
                </div>
                {isApproved(selectedRequest) ? (
                  <div className="bg-white rounded-lg p-4 border border-green-200">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Approved By</p>
                    <p className="text-lg font-bold text-gray-900">{selectedRequest.approvedBy}</p>
                    {selectedRequest.approvalDate && <p className="text-xs text-gray-500 mt-1">Approved on {formatDate(selectedRequest.approvalDate)}</p>}
                  </div>
                ) : selectedRequest.status === 'Rejected' ? (
                  <div className="bg-white rounded-lg p-4 border border-red-200">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Rejected By</p>
                    <p className="text-lg font-bold text-gray-900">{selectedRequest.rejectedBy}</p>
                    {selectedRequest.rejectionReason && (
                      <div className="mt-3 pt-3 border-t border-red-100">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Reason</p>
                        <p className="text-sm text-gray-800">{selectedRequest.rejectionReason}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-yellow-600" />
                      <p className="text-sm font-semibold text-gray-700">Awaiting approval decision</p>
                    </div>
                    <p className="text-sm text-gray-600">Review the details above and make your decision below.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t bg-gray-50 px-8 py-5 flex justify-between items-center gap-3 flex-shrink-0">
              <button onClick={() => exportToPDF(selectedRequest)}
                className="px-5 py-2.5 bg-gray-600 hover:bg-gray-700 text-white rounded-xl font-semibold transition-all shadow-md flex items-center gap-2">
                <Printer className="w-4 h-4" /> Export PDF
              </button>
              <div className="flex gap-3 items-center">
                {canApprove && !isApproved(selectedRequest) && selectedRequest.status !== 'Rejected' && (
                  <>
                    <button onClick={handleReject} disabled={approving}
                      className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-all shadow-md flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                      {approving ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</> : <><XCircle className="w-4 h-4" /> Reject</>}
                    </button>
                    <button onClick={handleApprove} disabled={approving}
                      className="px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold transition-all shadow-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                      {approving ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</> : <><CheckCircle className="w-4 h-4" /> Approve</>}
                    </button>
                  </>
                )}
                {!canApprove && !isApproved(selectedRequest) && selectedRequest.status !== 'Rejected' && (
                  <span className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-500 rounded-xl text-sm font-medium border border-gray-200">
                    <AlertCircle className="w-4 h-4" /> No approval permission
                  </span>
                )}
                <button onClick={() => setShowModal(false)}
                  className="px-6 py-2.5 bg-gray-600 hover:bg-gray-700 text-white rounded-xl font-semibold transition-all shadow-md">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}